import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import PageHeader from '../../components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, Target, CheckCircle2, Building2 } from 'lucide-react';

const ArchitectWorkflowIntelligence: React.FC = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Case Study: Professional Services Workflow Intelligence",
    "description": "How a New Zealand professional services firm used workflow intelligence to standardise intake, reduce coordination overhead, and improve project visibility.",
    "publisher": { "@type": "Organization", "name": "ThinkTomorrow.AI" },
    "url": "https://www.thinktomorrow.ai/case-studies/architect-workflow-intelligence"
  };

  return (
    <>
      <Helmet>
        <title>Case Study: Professional Services Workflow Intelligence | ThinkTomorrow.AI</title>
        <meta name="description" content="How a NZ professional services firm reduced coordination overhead and improved project visibility using workflow intelligence and responsible AI." />
        <link rel="canonical" href="https://www.thinktomorrow.ai/case-studies/architect-workflow-intelligence" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-24">
        <PageHeader
          title="Case Study: Professional Services Workflow Intelligence"
          subtitle="How workflow mapping and responsible automation improved coordination, scoping, and reporting for a New Zealand professional services firm."
        />

        {/* Client Context */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="text-brand-teal" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Client Context</h2>
            </div>
            <p className="text-muted-foreground text-lg mb-4">
              A mid-sized New Zealand professional services firm managing residential and commercial projects across multiple stages — from initial client engagement through to construction documentation and project close-out.
            </p>
            <p className="text-muted-foreground text-lg">
              The practice relied on experienced professionals but lacked standardised systems for intake, consultant coordination, and project status tracking. As the team grew, administrative overhead was consuming an increasing share of senior staff time.
            </p>
          </div>
        </section>

        {/* Challenges */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="text-amber-500" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Challenges</h2>
            </div>
            <div className="space-y-4">
              <div className="p-5 bg-background rounded-lg border">
                <h3 className="font-semibold text-foreground mb-2">Scoping Ambiguity</h3>
                <p className="text-muted-foreground">New project intake varied by team member. Without a standardised scoping process, some projects launched with incomplete briefs, leading to scope creep, rework, and difficult fee conversations later in the project.</p>
              </div>
              <div className="p-5 bg-background rounded-lg border">
                <h3 className="font-semibold text-foreground mb-2">Coordination Overhead</h3>
                <p className="text-muted-foreground">Managing sub-consultants (structural, services, landscape) required manual follow-up — chasing deliverables, confirming deadlines, and reconciling project timelines across multiple disciplines. This was consuming 6–10 hours per week of senior staff time.</p>
              </div>
              <div className="p-5 bg-background rounded-lg border">
                <h3 className="font-semibold text-foreground mb-2">Reporting Complexity</h3>
                <p className="text-muted-foreground">Project status reporting was manual and inconsistent. Directors needed visibility across all active projects but relied on ad-hoc updates, making it difficult to identify at-risk projects early.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Approach */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Target className="text-brand-teal" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Approach</h2>
            </div>
            <div className="space-y-6">
              {[
                {
                  title: "Workflow Mapping",
                  desc: "Documented the full project lifecycle from initial enquiry to project close-out, identifying where time was lost, where processes varied between staff, and where manual steps could be standardised or automated."
                },
                {
                  title: "Standardised Intake",
                  desc: "Designed a structured intake workflow capturing client requirements, site information, regulatory considerations, and scope parameters consistently — reducing ambiguity before projects entered the design phase."
                },
                {
                  title: "AI-Assisted Drafting",
                  desc: "Implemented AI tools to assist with preliminary scope documentation, meeting note summaries, and consultant brief preparation — reducing drafting time while maintaining professional quality and human review."
                },
                {
                  title: "Coordination Visibility",
                  desc: "Built automated tracking and notification systems for consultant deliverables, deadline management, and project milestone reporting — giving directors real-time visibility across all active projects."
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-brand-teal text-white flex items-center justify-center font-bold shrink-0 text-sm">{i + 1}</div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="text-brand-teal" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Results</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Standardised intake reduced scope ambiguity and improved fee accuracy on new projects",
                "Consultant coordination time reduced significantly, freeing senior staff capacity",
                "Directors gained real-time project visibility without relying on ad-hoc status updates",
                "AI-assisted drafting reduced preliminary documentation time while maintaining quality",
                "Team reported improved confidence in project timelines and deliverable tracking",
                "Governance framework ensured all AI outputs were reviewed before external use"
              ].map((result, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-background rounded-lg border">
                  <CheckCircle2 className="text-brand-teal mt-0.5 shrink-0" size={18} />
                  <span className="text-foreground text-sm">{result}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#0B1939] to-[#081631] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl text-center">
            <h2 className="text-3xl font-bold mb-4">See How Workflow Intelligence Can Help Your Firm</h2>
            <p className="text-gray-300 mb-8">Book a discovery call and we'll map your workflows, identify quick wins, and build a practical automation roadmap.</p>
            <Link to="/contact">
              <Button variant="primary" className="flex items-center gap-2 mx-auto">
                Book Discovery Call <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <h3 className="font-semibold text-foreground mb-4">Related Pages</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/ai-automation-professional-services" className="text-brand-teal hover:underline text-sm">AI Automation for Professional Services →</Link>
              <Link to="/workflow-automation-marlborough" className="text-brand-teal hover:underline text-sm">Workflow Automation Marlborough →</Link>
              <Link to="/ai-workflow-automation-nz" className="text-brand-teal hover:underline text-sm">AI Workflow Automation NZ →</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ArchitectWorkflowIntelligence;
